import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/SimpleAuthContext';
import { API_CONFIG } from '../config/apiConfig';
import { useAnnouncements } from '../contexts/AnnouncementsContext';
import ProfilePictureUpload from '../components/profile/ProfilePictureUpload';
import MatricSubjects from '../components/profile/MatricSubjects';
import TertiaryEducation from '../components/profile/TertiaryEducation';
import {
  validateSAIdNumber,
  autoFillFromSAId,
  formatIdNumber,
  getIdValidationMessage
} from '../utils/saIdValidation';
import { getProfilePictureUrl } from '../utils/imageUtils';
import api from '../services/apiClient';
import {
  validateEmail,
  validatePhoneNumber,
  validateField,
  formatPhoneNumberDisplay
} from '../utils/formValidation';
import {
  UserIcon,
  MapPinIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CpuChipIcon,
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  BellIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './Profile.css';
import { canonicalSkillCategory, skillCategoryLabel, lmsSkillCategoryKey, lmsSkillCategoryLabel,
  lmsSkillCategoryOptions, availableCatalogueSkills, filterCatalogueSkills } from '../config/skillCategories';

// Economic Corridor Mapping for Gauteng - 5 Province-wide Corridors
const getEconomicCorridor = (postalCode) => {
  if (!postalCode || postalCode.length < 4) return null;

  const code = parseInt(postalCode);

  // Gauteng Economic Corridors - 5 province-wide corridors
  // Based on official GGDA (Gauteng Growth and Development Agency) designations

  // Northern Corridor - Tshwane (Pretoria) and northern Gauteng
  if (code >= 1 && code <= 999) {
    return 'North';    // Pretoria/Tshwane areas (0001-0999)
  } else if (code >= 2100 && code <= 2199) {
    return 'North';    // Northern Johannesburg (Sandton, Randburg)

  // Southern Corridor - Vaal Triangle and southern Gauteng
  } else if (code >= 1900 && code <= 1999) {
    return 'South';    // Vaal Triangle (Vanderbijlpark, Vereeniging)
  } else if (code >= 1800 && code <= 1899) {
    return 'South';    // Southern areas (Soweto, Lenasia, Orange Farm)

  // Eastern Corridor - Ekurhuleni and eastern Gauteng
  } else if (code >= 1400 && code <= 1699) {
    return 'East';     // Ekurhuleni (Germiston, Benoni, Boksburg, Springs)

  // Western Corridor - West Rand and western Gauteng
  } else if (code >= 1700 && code <= 1799) {
    return 'West';     // West Rand (Krugersdorp, Roodepoort, Randfontein)
  } else if (code >= 2200 && code <= 2699) {
    return 'West';     // Extended West Rand areas

  // Central Corridor - Central Johannesburg and surrounds
  } else if (code >= 1000 && code <= 1399) {
    return 'Central';  // Johannesburg CBD, Newtown, Braamfontein
  } else if (code >= 2000 && code <= 2099) {
    return 'Central';  // Central Johannesburg (includes 2094 Cleveland)

  } else {
    return 'Other Gauteng Area';
  }
};

const Profile = () => {
  const { user, refreshUser, accessToken } = useAuth();
  const {
    pushNotificationEnabled,
    enablePushNotifications,
    disablePushNotifications
  } = useAnnouncements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  
  const [formData, setFormData] = useState({
    // Personal Information
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    id_number: user?.id_number || '',
    date_of_birth: user?.date_of_birth || '',
    gender: user?.gender || '',
    race: user?.race || '',
    province: user?.province || '',
    city: user?.city || '',
    address: user?.address || '',
    postal_code: user?.postal_code || '',
    economic_corridor: user?.economic_corridor || '',
    bio: user?.bio || '',
    // Basic Education
    highest_grade: user?.highest_grade || '',
    has_matric: user?.has_matric || false,
    matric_year: user?.matric_year || '',
    school_name: user?.school_name || '',
    additional_certificates: user?.additional_certificates || '',
    // Tertiary Education
    tertiary_qualification_type: user?.tertiary_qualification_type || '',
    tertiary_institution: user?.tertiary_institution || '',
    field_of_study: user?.field_of_study || '',
    tertiary_year_completed: user?.tertiary_year_completed || '',
    tertiary_qualification_status: user?.tertiary_qualification_status || '',
    additional_qualifications: user?.additional_qualifications || ''
  });

  // Location state
  const [locationData, setLocationData] = useState({
    latitude: user?.latitude || null,
    longitude: user?.longitude || null,
    accuracy: user?.location_accuracy || null,
    timestamp: user?.location_timestamp || null,
    consent: user?.location_consent || false,
    nearest_area: user?.nearest_area || ''
  });
  const [locationLoading, setLocationLoading] = useState(false);

  // Profile picture state
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(user?.profile_picture_url || null);

  // Registrations state
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(true);

  // ID validation state
  const [idValidation, setIdValidation] = useState({
    isValid: null,
    errors: [],
    autoFilled: false,
    serverError: null
  });

  // Field validation state
  const [fieldValidation, setFieldValidation] = useState({
    email: { isValid: null, error: null },
    phone_number: { isValid: null, error: null }
  });

  // Skills state
  const [skills, setSkills] = useState([]);
  const [userSkills, setUserSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [userSkillsLoading, setUserSkillsLoading] = useState(false);
  const [skillSearch, setSkillSearch] = useState('');
  const [modalSkillSearch, setModalSkillSearch] = useState('');
  const [skillsView, setSkillsView] = useState('owned');
  const [catalogueLimit, setCatalogueLimit] = useState(24);
  const [catalogueError, setCatalogueError] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchContainerRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [newSkill, setNewSkill] = useState({
    skill: '',
    custom_skill_name: '',
    proficiency_level: '',
    years_experience: '',
    notes: ''
  });

  // Enhanced Skills Modal States
  const [skillModalLoading, setSkillModalLoading] = useState(false);
  const [skillModalError, setSkillModalError] = useState('');
  const [skillModalSuccess, setSkillModalSuccess] = useState('');
  const [skillType, setSkillType] = useState('predefined'); // 'predefined' or 'custom'
  const [skillValidation, setSkillValidation] = useState({
    skill: { isValid: true, error: '' },
    custom_skill_name: { isValid: true, error: '' },
    proficiency_level: { isValid: true, error: '' },
    years_experience: { isValid: true, error: '' }
  });

  // Push notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState({
    push_notifications: pushNotificationEnabled, // Sync with actual status
    course_deadlines: true,
    system_updates: true,
    training_opportunities: true,
    government_initiatives: true,
    general_news: false
  });
  const [notificationLoading, setNotificationLoading] = useState(false);

  // CV Editing State
  const [cvHtml, setCvHtml] = useState('');
  const [isEditingCv, setIsEditingCv] = useState(false);
  const [isGeneratingCustomCv, setIsGeneratingCustomCv] = useState(false);
  const [cvLoading, setCvLoading] = useState(false);

  // Handle entering edit mode for CV
  const handleEditCv = async () => {
    setCvLoading(true);
    try {
      // Add timestamp to prevent caching
      const response = await makeAuthenticatedRequest(`/api/auth/cv/?cv_format=html&editable=true&t=${new Date().getTime()}`);
      if (response.ok) {
        const data = await response.json();
        setCvHtml(data.html);
        setIsEditingCv(true);
      } else {
        console.error('Failed to fetch CV HTML');
        alert('Failed to load CV for editing. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching CV HTML:', error);
      alert('An error occurred while loading the CV.');
    } finally {
      setCvLoading(false);
    }
  };

  // Handle saving/generating custom CV PDF
  const handleSaveCustomCv = async () => {
    setIsGeneratingCustomCv(true);
    try {
      const response = await makeAuthenticatedRequest('/api/auth/cv/custom/', {
        method: 'POST',
        body: JSON.stringify({ html_content: cvHtml })
      });

      if (response.ok) {
        // Handle PDF download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `CV_${user?.first_name || 'User'}_${user?.last_name || ''}_Custom.pdf`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        document.body.removeChild(a);
        // Optional: Exit edit mode after successful download
        // setIsEditingCv(false); 
      } else {
        console.error('Failed to generate custom CV');
        alert('Failed to generate PDF. Please try again.');
      }
    } catch (error) {
      console.error('Error generating custom CV:', error);
      alert('An error occurred while generating the PDF.');
    } finally {
      setIsGeneratingCustomCv(false);
    }
  };

  const handleCancelEditCv = () => {
    setIsEditingCv(false);
    setCvHtml('');
  };

  // Update profile picture preview when user data changes
  useEffect(() => {
    if (user?.profile_picture_url && !profilePicture) {
      setProfilePicturePreview(user.profile_picture_url);
    }
  }, [user?.profile_picture_url, profilePicture]);

  // Sync notification preferences with push notification status
  useEffect(() => {
    setNotificationPreferences(prev => ({
      ...prev,
      push_notifications: pushNotificationEnabled
    }));
  }, [pushNotificationEnabled]);

  // Handle outside click for skill dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Utility function to make API calls
  const makeAPICall = async (endpoint, options = {}) => {
    console.log(`🔄 Making API call to: ${endpoint}`);
    console.log(`🔧 Request options:`, options);

    // Use direct URL since proxy is not working
    const directUrl = `http://localhost:8000${endpoint}`;
    console.log(`🔄 Direct URL: ${directUrl}`);

    try {
      const response = await fetch(directUrl, options);
      console.log(`📡 Direct response:`, {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (response.ok) {
        console.log(`✅ Direct call successful for ${endpoint}`);
      } else {
        console.log(`❌ Direct call failed for ${endpoint}: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error(`❌ Direct call failed for ${endpoint}:`, error);
      throw error;
    }
  };

  // Simplified authentication helper for Profile
  const makeAuthenticatedRequest = async (url, options = {}) => {
    console.log('🔐 makeAuthenticatedRequest called:', { url, method: options.method });

    // Ensure URL is absolute
    const fullUrl = url.startsWith('http') ? url : `http://localhost:8000${url}`;
    const method = (options.method || 'GET').toUpperCase();
    
    // Helper to perform fetch
    const doFetch = async (authHeader) => {
        const headers = {
            ...(options.headers || {})
        };

        const hasContentType = Object.keys(headers).some((key) => key.toLowerCase() === 'content-type');
        const shouldSendJsonContentType = method !== 'GET' && method !== 'HEAD' && options.body && !hasContentType;
        if (shouldSendJsonContentType) {
            headers['Content-Type'] = 'application/json';
        }

        if (authHeader) {
            headers['Authorization'] = authHeader;
        }
        
        // Get CSRF token for session fallback
        const csrfToken = document.querySelector('[name=csrfmiddlewaretoken]')?.value ||
                         document.cookie.split('; ').find(row => row.startsWith('csrftoken='))?.split('=')[1];

        if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
        }

        try {
            const response = await fetch(fullUrl, {
                method,
                headers: headers,
                body: method === 'GET' || method === 'HEAD' ? undefined : options.body,
                credentials: 'include'
            });
            return response;
        } catch (error) {
            console.error('Fetch error:', error);
            throw error;
        }
    };

    // Strategy 1: JWT (access_token)
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
        console.log('🔐 Trying Strategy 1: JWT');
        const res = await doFetch(`Bearer ${accessToken}`);
        if (res.status !== 401) return res;
        console.log('🔐 JWT failed (401), clearing and falling back...');
        localStorage.removeItem('access_token');
    }

    // Strategy 2: DRF Token (token)
    const token = localStorage.getItem('token');
    if (token) {
        console.log('🔐 Trying Strategy 2: DRF Token');
        const res = await doFetch(`Token ${token}`);
        if (res.status !== 401) return res;
        console.log('🔐 DRF Token failed (401), clearing and falling back...');
        localStorage.removeItem('token');
    }

    // Strategy 3: Session (no auth header)
    console.log('🔐 Trying Strategy 3: Session (Cookies)');
    return doFetch(null);
  };

  // Fetch user registrations
  const fetchRegistrations = async () => {
    try {
      setRegistrationsLoading(true);
      const response = await makeAuthenticatedRequest('/api/registrations/my-registrations/');

      if (response.ok) {
        const data = await response.json();
        setRegistrations(data);
      } else {
        console.error('Failed to fetch registrations:', response.status);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
    } finally {
      setRegistrationsLoading(false);
    }
  };

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone_number: user.phone_number || '',
        id_number: user.id_number || '',
        date_of_birth: user.date_of_birth || '',
        gender: user.gender || '',
        race: user.race || '',
        province: user.province || '',
        city: user.city || '',
        address: user.address || '',
        postal_code: user.postal_code || '',
      economic_corridor: user?.economic_corridor || '',
        bio: user.bio || '',
        highest_grade: user.highest_grade || '',
        has_matric: user.has_matric || false,
        matric_year: user.matric_year || '',
        school_name: user.school_name || '',
        additional_certificates: user.additional_certificates || '',
        tertiary_qualification_type: user.tertiary_qualification_type || '',
        tertiary_institution: user.tertiary_institution || '',
        field_of_study: user.field_of_study || '',
        tertiary_year_completed: user.tertiary_year_completed || '',
        tertiary_qualification_status: user.tertiary_qualification_status || '',
        additional_qualifications: user.additional_qualifications || ''
      });

      setLocationData({
        latitude: user.latitude || null,
        longitude: user.longitude || null,
        accuracy: user.location_accuracy || null,
        timestamp: user.location_timestamp || null,
        consent: user.location_consent || false,
        nearest_area: user.nearest_area || ''
      });

      // Update profile picture preview
      setProfilePicturePreview(user.profile_picture_url || null);

      // Fetch registrations
      fetchRegistrations();
    }
  }, [user]);

  // Ensure user has a token for API access
  const ensureUserToken = async () => {
    return accessToken || localStorage.getItem('access_token');
  };

  // Fetch skills when skills section is active
  useEffect(() => {
    if (activeSection === 'skills') {
      ensureUserToken().then(() => {
        fetchSkills();
        fetchUserSkills();
      });
    }
  }, [activeSection]);

  // Fetch user skills on component mount and when user changes
  useEffect(() => {
    if (user) {
      console.log('🔄 User changed, fetching user skills...');
      ensureUserToken().then(() => {
        fetchUserSkills();
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: newValue
      };

      // Auto-update economic corridor when postal code changes
      if (name === 'postal_code') {
        updated.economic_corridor = getEconomicCorridor(newValue);
      }

      // Handle ID number validation and auto-fill
      if (name === 'id_number') {
        handleIdNumberChange(newValue, updated);
      }

      // Handle field validation for email and phone
      if (name === 'email' || name === 'phone_number') {
        handleFieldValidation(name, newValue);
      }

      return updated;
    });
  };

  // Handle field validation
  const handleFieldValidation = (fieldName, value) => {
    const validation = validateField(fieldName, value);

    setFieldValidation(prev => ({
      ...prev,
      [fieldName]: {
        isValid: validation.isValid,
        error: validation.error
      }
    }));

    // Update formatted value for phone number
    if (fieldName === 'phone_number' && validation.formatted && validation.formatted !== value) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: validation.formatted
      }));
    }
  };

  // Handle ID number validation and auto-fill
  const handleIdNumberChange = (idNumber, formDataToUpdate) => {
    // Clear previous validation including server errors
    setIdValidation({
      isValid: null,
      errors: [],
      autoFilled: false,
      serverError: null
    });

    // If ID number is empty, clear validation
    if (!idNumber || idNumber.trim() === '') {
      return;
    }

    // Validate the ID number
    const validation = validateSAIdNumber(idNumber);

    if (validation.isValid) {
      // Auto-fill date of birth and gender silently if they're empty
      const autoFillData = autoFillFromSAId(idNumber);

      if (autoFillData) {
        // Always auto-fill date of birth and gender from valid ID number
        formDataToUpdate.date_of_birth = autoFillData.date_of_birth;
        formDataToUpdate.gender = autoFillData.gender;

        setIdValidation({
          isValid: true,
          errors: [],
          autoFilled: false, // Don't show auto-fill notifications
          serverError: null
        });
      }
    } else {
      setIdValidation({
        isValid: false,
        errors: validation.errors,
        autoFilled: false,
        serverError: null
      });
    }
  };

  // Profile picture handlers
  const handleProfilePictureChange = (file) => {
    console.log('📸 Profile Picture Change:', {
      file: file,
      name: file?.name,
      size: file?.size,
      type: file?.type
    });
    setProfilePicture(file);

    // Create preview URL for immediate feedback
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setProfilePicturePreview(previewUrl);
      console.log('📸 Profile Picture Preview URL created:', previewUrl);
    }
  };

  const handleProfilePictureRemove = () => {
    console.log('📸 Profile Picture: Removing picture');
    setProfilePicture(null);
    setProfilePicturePreview(null);
  };

  // Skills functions
  const fetchAllSkillPages = async (initialUrl) => {
    const collectedItems = [];
    let nextUrl = initialUrl;
    let lastResponse = null;

    for (let page = 0; nextUrl && page < 100; page += 1) {
      lastResponse = await makeAuthenticatedRequest(nextUrl);
      if (!lastResponse.ok) {
        return { response: lastResponse, items: collectedItems };
      }

      const pageData = await lastResponse.json();
      const pageItems = Array.isArray(pageData)
        ? pageData
        : pageData?.results || pageData?.skills || pageData?.user_skills || pageData?.data || [];

      if (Array.isArray(pageItems)) {
        collectedItems.push(...pageItems);
      }

      nextUrl = Array.isArray(pageData) ? null : pageData?.next || null;
    }

    return { response: lastResponse, items: collectedItems };
  };

  const fetchSkills = async () => {
    try {
      setSkillsLoading(true);
      setCatalogueError('');
      console.log('🔍 Fetching skills from auth API...');

      // Use the auth skills API to get compatible skills list
      const { response, items: rawSkills } = await fetchAllSkillPages(`${API_CONFIG.API_URL}/auth/skills/`);

      console.log('📡 Skills API Response:', {
        status: response?.status,
        statusText: response?.statusText,
        ok: response?.ok,
        url: response?.url
      });

      if (response?.ok) {
        // Normalize all supported response shapes and remove duplicate IDs.
        const allSkills = rawSkills.map(skill => ({
          id: skill.id,
          name: skill.name,
          description: skill.description,
          category: skill.category,
          lms_category: skill.lms_category,
          lms_category_display: skill.lms_category_display,
          category_display: skill.category_display || skill.category_name || skill.category,
          is_active: skill.is_active
        })).filter(skill => skill.id && skill.name);

        const uniqueSkills = Array.from(
          new Map(allSkills.map(skill => [skill.id, skill])).values()
        ).sort((firstSkill, secondSkill) => firstSkill.name.localeCompare(secondSkill.name));

        setSkills(uniqueSkills);
        console.log('✅ Skills processed:', uniqueSkills.length, 'skills');
      } else {
        console.error('❌ Failed to fetch skills:', response.status, response.statusText);
        setCatalogueError('The shared skills catalogue could not be loaded. Please try again.');
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
      }
    } catch (error) {
      console.error('❌ Error fetching skills:', error);
      setCatalogueError('The shared skills catalogue could not be loaded. Please try again.');
    } finally {
      setSkillsLoading(false);
    }
  };

  const fetchUserSkills = async () => {
    try {
      setUserSkillsLoading(true);
      console.log('🔍 Fetching user skills...');
      const { response, items: userSkillsArray } = await fetchAllSkillPages(`${API_CONFIG.API_URL}/auth/user-skills/`);

      console.log('📡 User Skills API Response:', {
        status: response?.status,
        statusText: response?.statusText,
        ok: response?.ok,
        url: response?.url
      });

      if (response?.ok) {
        setUserSkills(userSkillsArray);
        console.log('✅ User skills fetched and set:', userSkillsArray.length, 'skills');
        console.log('✅ User skills data:', userSkillsArray);
      } else {
        console.error('❌ Failed to fetch user skills:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
      }
    } catch (error) {
      console.error('❌ Error fetching user skills:', error);
    } finally {
      setUserSkillsLoading(false);
    }
  };

  // Enhanced skill validation function
  const validateSkillForm = () => {
    const validation = {
      skill: { isValid: true, error: '' },
      custom_skill_name: { isValid: true, error: '' },
      proficiency_level: { isValid: true, error: '' },
      years_experience: { isValid: true, error: '' }
    };

    let isFormValid = true;

    if (!newSkill.proficiency_level) {
      validation.proficiency_level = { isValid: false, error: 'Please choose your proficiency level' };
      isFormValid = false;
    }

    // Validate skill selection or custom skill name based on skillType
    if (skillType === 'predefined') {
      if (!newSkill.skill) {
        validation.skill = { isValid: false, error: 'Please select a skill from the list' };
        isFormValid = false;
      }
    } else if (skillType === 'custom') {
      if (!newSkill.custom_skill_name.trim()) {
        validation.custom_skill_name = { isValid: false, error: 'Please enter a custom skill name' };
        isFormValid = false;
      } else if (newSkill.custom_skill_name.trim().length < 2) {
        validation.custom_skill_name = { isValid: false, error: 'Skill name must be at least 2 characters long' };
        isFormValid = false;
      }
    }

    // Validate years of experience if provided
    if (newSkill.years_experience && (newSkill.years_experience < 0 || newSkill.years_experience > 50)) {
      validation.years_experience = { isValid: false, error: 'Years of experience must be between 0 and 50' };
      isFormValid = false;
    }

    setSkillValidation(validation);
    return isFormValid;
  };

  const addUserSkill = async (skillData) => {
    // Clear previous errors and set loading state
    setSkillModalError('');
    setSkillModalSuccess('');
    setSkillModalLoading(true);

    // Validate form before submission
    if (!validateSkillForm()) {
      setSkillModalLoading(false);
      setSkillModalError('Please fix the validation errors above');
      return;
    }

    try {
      console.log('🚀 Adding skill:', skillData);
      console.log('🔐 Auth check - Token exists:', !!localStorage.getItem('token'));
      console.log('🔐 Auth check - User:', user?.email);
      console.log('🔍 Skill data being sent:', JSON.stringify(skillData, null, 2));
      console.log('🔍 Skill type:', skillType);
      console.log('🔍 New skill state:', JSON.stringify(newSkill, null, 2));

      // Prepare clean data for backend
      const cleanSkillData = {};

      if (skillType === 'predefined') {
        // For predefined skills, we need the skill ID
        if (skillData.skill && skillData.skill !== '') {
          cleanSkillData.skill = parseInt(skillData.skill, 10);
        } else {
          throw new Error('Please select a skill from the list');
        }
      } else if (skillType === 'custom') {
        // For custom skills, we need the custom skill name (no skill ID)
        if (skillData.custom_skill_name && skillData.custom_skill_name.trim() !== '') {
          cleanSkillData.custom_skill_name = skillData.custom_skill_name.trim();
        } else {
          throw new Error('Please enter a custom skill name');
        }
      }

      // Add other fields if they have values
      if (skillData.proficiency_level) {
        cleanSkillData.proficiency_level = skillData.proficiency_level;
      }

      if (skillData.years_experience && skillData.years_experience !== '') {
        cleanSkillData.years_experience = parseInt(skillData.years_experience, 10);
      }

      if (skillData.notes && skillData.notes.trim() !== '') {
        cleanSkillData.notes = skillData.notes.trim();
      }

      console.log('🔍 Clean skill data being sent:', JSON.stringify(cleanSkillData, null, 2));

      // Ensure we have a valid token before making the request
      await ensureUserToken();

      const response = await makeAuthenticatedRequest('/api/auth/user-skills/', {
        method: 'POST',
        body: JSON.stringify(cleanSkillData)
      });

      console.log('📡 API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries())
      });

      // Check if response is HTML (error page) instead of JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        const htmlText = await response.text();
        console.error('❌ Received HTML response instead of JSON:', htmlText.substring(0, 200));
        throw new Error('Server returned HTML error page instead of JSON. Check backend logs.');
      }

      if (response.ok) {
        const newSkillResponse = await response.json();
        console.log('✅ Skill added successfully:', newSkillResponse);

        // Update user skills list
        setUserSkills(prev => [...prev, newSkillResponse]);

        // Show success message
        setSkillModalSuccess(`🎉 Skill "${newSkillResponse.skill_name || newSkillResponse.custom_skill_name}" added successfully!`);

        // Reset form after a short delay to show success message
        setTimeout(() => {
          setShowAddSkill(false);
          setNewSkill({
            skill: '',
            custom_skill_name: '',
            proficiency_level: '',
            years_experience: '',
            notes: ''
          });
          setSkillType('predefined');
          setSkillModalSuccess('');
          setSkillModalError('');
          setSkillValidation({
            skill: { isValid: true, error: '' },
            custom_skill_name: { isValid: true, error: '' },
            proficiency_level: { isValid: true, error: '' },
            years_experience: { isValid: true, error: '' }
          });
        }, 2000);

      } else {
        let errorData;
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.error('❌ Failed to parse error response as JSON:', parseError);
          const errorText = await response.text();
          console.error('❌ Raw error response:', errorText);
          setSkillModalError(`Server error (${response.status}): ${response.statusText}. Please try again.`);
          return;
        }

        console.error('❌ Error adding skill:', errorData);
        console.error('❌ Full error response structure:', JSON.stringify(errorData, null, 2));

        // Handle specific error types
        if (errorData.errors) {
          console.log('🔍 Error has errors property:', errorData.errors);
          if (errorData.errors.skill) {
            setSkillModalError(`Skill Error: ${Array.isArray(errorData.errors.skill) ? errorData.errors.skill[0] : errorData.errors.skill}`);
          } else if (errorData.errors.custom_skill_name) {
            setSkillModalError(`Custom Skill Error: ${Array.isArray(errorData.errors.custom_skill_name) ? errorData.errors.custom_skill_name[0] : errorData.errors.custom_skill_name}`);
          } else if (errorData.errors.non_field_errors) {
            const errorMsg = Array.isArray(errorData.errors.non_field_errors) ? errorData.errors.non_field_errors[0] : errorData.errors.non_field_errors;
            console.log('🔍 Non-field error:', errorMsg);
            // Check for duplicate skill error
            if (errorMsg.includes('already exists') || errorMsg.includes('unique') || errorMsg.includes('duplicate')) {
              setSkillModalError('You have already added this skill to your profile. Please select a different skill.');
            } else {
              setSkillModalError(errorMsg);
            }
          } else {
            console.log('🔍 Unknown error structure in errors:', Object.keys(errorData.errors));
            setSkillModalError(`Error: ${JSON.stringify(errorData.errors)}`);
          }
        } else if (errorData.message) {
          console.log('🔍 Error has message property:', errorData.message);
          setSkillModalError(errorData.message);
        } else if (errorData.detail) {
          console.log('🔍 Error has detail property:', errorData.detail);
          // Handle Django REST framework error format
          if (errorData.detail.includes('already exists') || errorData.detail.includes('unique') || errorData.detail.includes('duplicate')) {
            setSkillModalError('You have already added this skill to your profile. Please select a different skill.');
          } else {
            setSkillModalError(errorData.detail);
          }
        } else if (Array.isArray(errorData.non_field_errors)) {
          console.log('🔍 Error has non_field_errors array:', errorData.non_field_errors);
          const errorMsg = errorData.non_field_errors[0];
          if (errorMsg.includes('already exists') || errorMsg.includes('unique') || errorMsg.includes('duplicate')) {
            setSkillModalError('You have already added this skill to your profile. Please select a different skill.');
          } else {
            setSkillModalError(errorMsg);
          }
        } else {
          console.log('🔍 Unknown error response format. Full response:', errorData);
          // Show a more user-friendly error message but log the details
          setSkillModalError(`Failed to add skill. Server response: ${JSON.stringify(errorData).substring(0, 100)}...`);
        }
      }
    } catch (error) {
      console.error('❌ Network error adding skill:', error);
      setSkillModalError('Network error. Please check your connection and try again.');
    } finally {
      setSkillModalLoading(false);
    }
  };

  // Reset skill modal state
  const resetSkillModal = () => {
    setModalSkillSearch('');
    setNewSkill({
      skill: '',
      custom_skill_name: '',
      proficiency_level: '',
      years_experience: '',
      notes: ''
    });
    setSkillType('predefined');
    setSkillModalError('');
    setSkillModalSuccess('');
    setSkillModalLoading(false);
    setEditingSkill(null);
    setSkillValidation({
      skill: { isValid: true, error: '' },
      custom_skill_name: { isValid: true, error: '' },
      proficiency_level: { isValid: true, error: '' },
      years_experience: { isValid: true, error: '' }
    });
  };

  // Handle skill form submission (add or edit)
  const handleSkillSubmit = () => {
    if (editingSkill) {
      updateUserSkill(newSkill);
    } else {
      addUserSkill(newSkill);
    }
  };

  // Open skill modal
  const openSkillModal = () => {
    resetSkillModal();
    setShowAddSkill(true);
  };

  // Close skill modal
  const closeSkillModal = () => {
    setShowAddSkill(false);
    resetSkillModal();
  };

  const removeUserSkill = async (skillId) => {
    try {
      const response = await makeAuthenticatedRequest(`/api/auth/user-skills/${skillId}/`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setUserSkills(prev => prev.filter(skill => skill.id !== skillId));
        console.log('✅ Skill removed successfully!');
      } else {
        console.error('❌ Failed to remove skill:', response.status);
      }
    } catch (error) {
      console.error('❌ Error removing skill:', error);
    }
  };

  // Edit skill function
  const editUserSkill = (userSkill) => {
    setEditingSkill(userSkill);
    setModalSkillSearch(userSkill.custom_skill_name || userSkill.skill_name || '');

    // Determine skill type based on whether it has a predefined skill or custom name
    const isCustomSkill = !userSkill.skill && userSkill.custom_skill_name;
    setSkillType(isCustomSkill ? 'custom' : 'predefined');

    // Populate form with existing skill data
    setNewSkill({
      skill: userSkill.skill || '',
      custom_skill_name: userSkill.custom_skill_name || '',
      proficiency_level: userSkill.proficiency_level || '',
      years_experience: userSkill.years_experience || '',
      notes: userSkill.notes || ''
    });

    // Reset validation
    setSkillValidation({
      skill: { isValid: true, error: '' },
      custom_skill_name: { isValid: true, error: '' },
      proficiency_level: { isValid: true, error: '' },
      years_experience: { isValid: true, error: '' }
    });

    // Clear modal messages
    setSkillModalError('');
    setSkillModalSuccess('');

    // Open modal
    setShowAddSkill(true);
  };

  // Update skill function
  const updateUserSkill = async (skillData) => {
    // Clear previous errors and set loading state
    setSkillModalError('');
    setSkillModalSuccess('');
    setSkillModalLoading(true);

    // Validate form before submission
    if (!validateSkillForm()) {
      setSkillModalLoading(false);
      setSkillModalError('Please fix the validation errors above');
      return;
    }

    try {
      console.log('🚀 Updating skill:', skillData);
      console.log('🔍 Editing skill ID:', editingSkill.id);

      // Prepare clean data for backend
      const cleanSkillData = {};

      if (skillType === 'predefined') {
        // For predefined skills, we need the skill ID
        if (skillData.skill && skillData.skill !== '') {
          cleanSkillData.skill = parseInt(skillData.skill, 10);
        } else {
          throw new Error('Please select a skill from the list');
        }
        // Clear custom skill name for predefined skills
        cleanSkillData.custom_skill_name = '';
      } else if (skillType === 'custom') {
        // For custom skills, we need the custom skill name
        if (skillData.custom_skill_name && skillData.custom_skill_name.trim() !== '') {
          cleanSkillData.custom_skill_name = skillData.custom_skill_name.trim();
        } else {
          throw new Error('Please enter a custom skill name');
        }
        // Clear skill ID for custom skills
        cleanSkillData.skill = null;
      }

      // Add proficiency level (required)
      cleanSkillData.proficiency_level = skillData.proficiency_level;

      // Add optional fields only if they have values
      if (skillData.years_experience && skillData.years_experience !== '') {
        cleanSkillData.years_experience = parseInt(skillData.years_experience, 10);
      }

      if (skillData.notes && skillData.notes.trim() !== '') {
        cleanSkillData.notes = skillData.notes.trim();
      }

      console.log('🔍 Clean skill data being sent:', JSON.stringify(cleanSkillData, null, 2));

      // Ensure we have a valid token before making the request
      await ensureUserToken();

      const response = await makeAuthenticatedRequest(`/api/auth/user-skills/${editingSkill.id}/`, {
        method: 'PUT',
        body: JSON.stringify(cleanSkillData)
      });

      console.log('📡 API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (response.ok) {
        const updatedSkill = await response.json();
        console.log('✅ Skill updated successfully:', updatedSkill);

        // Update the user skills list
        setUserSkills(prev => prev.map(skill =>
          skill.id === editingSkill.id ? updatedSkill : skill
        ));

        setSkillModalSuccess('Skill updated successfully! 🎉');

        // Close modal after a short delay
        setTimeout(() => {
          closeSkillModal();
        }, 1500);

      } else {
        // Handle API errors
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ API Error Response:', errorData);

        if (response.status === 400 && errorData.non_field_errors) {
          setSkillModalError(errorData.non_field_errors[0]);
        } else if (response.status === 400) {
          // Handle field-specific errors
          const errorMessages = [];
          Object.entries(errorData).forEach(([field, errors]) => {
            if (Array.isArray(errors)) {
              errorMessages.push(`${field}: ${errors.join(', ')}`);
            }
          });
          setSkillModalError(errorMessages.join('; ') || 'Please check your input and try again');
        } else {
          setSkillModalError(`Failed to update skill (${response.status}). Please try again.`);
        }
      }
    } catch (error) {
      console.error('❌ Error updating skill:', error);
      setSkillModalError(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setSkillModalLoading(false);
    }
  };

  const validateForm = () => {
    // Validate required fields
    if (!formData.first_name.trim()) {
      setError('First name is required');
      return false;
    }

    if (!formData.last_name.trim()) {
      setError('Last name is required');
      return false;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }

    // Validate email format
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      return false;
    }

    // Validate phone number if provided
    if (formData.phone_number && formData.phone_number.trim() !== '') {
      const phoneValidation = validatePhoneNumber(formData.phone_number);
      if (!phoneValidation.isValid) {
        setError(phoneValidation.error);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (!validateForm()) {
      setSubmitting(false);
      return;
    }

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();

      // Filter out read-only fields that shouldn't be sent to backend
      const readOnlyFields = ['email']; // Email is read-only in the backend
      const allowedFields = Object.keys(formData).filter(key => !readOnlyFields.includes(key));

      // Add only allowed form fields
      allowedFields.forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      console.log('🔧 Profile Update: Filtered out read-only fields:', readOnlyFields);
      console.log('🔧 Profile Update: Sending fields:', allowedFields.filter(key => formData[key] !== null && formData[key] !== ''));

      // Add profile picture if selected
      if (profilePicture) {
        console.log('📸 Profile Picture: Adding to form data:', {
          name: profilePicture.name,
          size: profilePicture.size,
          type: profilePicture.type
        });
        formDataToSend.append('profile_picture', profilePicture);
      } else {
        console.log('📸 Profile Picture: No new picture selected');
      }

      // Debug FormData contents
      console.log('📋 Form Data Contents:');
      for (let [key, value] of formDataToSend.entries()) {
        if (key === 'profile_picture') {
          console.log(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }

      console.log('🔄 Profile Update: Starting update process...');

      let response;
      let result;

      try {
        const apiResponse = await api.patch('/auth/profile/', formDataToSend);
        response = { ok: true, status: apiResponse.status };
        result = apiResponse.data;
      } catch (requestError) {
        if (!requestError.response) throw requestError;
        response = { ok: false, status: requestError.response.status };
        result = requestError.response.data || {};
      }

      if (response.ok) {
        console.log('✅ Profile Update: Success response received:', result);

        // Scroll to top when showing success message
        if (typeof window.scrollTo === 'function') {
          window.scrollTo(0, 0);
        }

        // Check what was updated
        const updatedFields = [];
        if (formData.first_name !== user?.first_name) updatedFields.push('First Name');
        if (formData.last_name !== user?.last_name) updatedFields.push('Last Name');
        if (formData.email !== user?.email) updatedFields.push('Email');
        if (formData.phone_number !== user?.phone_number) updatedFields.push('Phone Number');
        if (profilePicture) updatedFields.push('Profile Picture');
        if (formData.id_number !== user?.id_number) updatedFields.push('ID Number');
        if (formData.date_of_birth !== user?.date_of_birth) updatedFields.push('Date of Birth');
        if (formData.gender !== user?.gender) updatedFields.push('Gender');
        if (formData.address !== user?.address) updatedFields.push('Address');
        if (formData.postal_code !== user?.postal_code) updatedFields.push('Postal Code');

        // Set detailed success message
        setSuccess({
          show: true,
          message: result.message || 'Profile updated successfully',
          updatedFields: updatedFields,
          profilePictureUpdated: !!profilePicture
        });

        setTimeout(() => {
          setSuccess(false);
        }, 8000); // Longer timeout for detailed message

        // Refresh user data from server
        console.log('🔄 Profile Update: Refreshing user data...');
        try {
          const refreshResult = await refreshUser();
          if (refreshResult.success) {
            console.log('✅ Profile Update: User data refreshed successfully');
            console.log('✅ Updated user data:', refreshResult.data);
          } else {
            console.log('❌ Profile Update: Failed to refresh user data:', refreshResult.error);
            // Don't show error to user since main profile save was successful
          }
        } catch (refreshError) {
          console.log('❌ Profile Update: Error refreshing user data:', refreshError);
          // Don't show error to user since main profile save was successful
        }

        // Reset profile picture state and update preview
        setProfilePicture(null);
        if (result.user?.profile_picture_url) {
          console.log('✅ Profile Picture Updated:', result.user.profile_picture_url);
          setProfilePicturePreview(result.user.profile_picture_url);
        }
      } else {
        // Handle specific error types
        let errorMessage = 'Failed to update profile. Please try again.';

        if (result.errors) {
          // Check for ID number uniqueness error
          if (result.errors.id_number) {
            const idError = Array.isArray(result.errors.id_number)
              ? result.errors.id_number[0]
              : result.errors.id_number;

            if (idError.includes('already exists') || idError.includes('unique')) {
              const duplicateMessage = 'This ID number is already registered by another user. Please check your ID number or contact support if you believe this is an error.';
              errorMessage = duplicateMessage;

              // Also set the ID validation error
              setIdValidation(prev => ({
                ...prev,
                isValid: false,
                serverError: duplicateMessage
              }));
            } else {
              errorMessage = `ID Number: ${idError}`;
              setIdValidation(prev => ({
                ...prev,
                isValid: false,
                serverError: idError
              }));
            }
          }
          // Check for email uniqueness error
          else if (result.errors.email) {
            const emailError = Array.isArray(result.errors.email)
              ? result.errors.email[0]
              : result.errors.email;

            if (emailError.includes('already exists') || emailError.includes('unique')) {
              errorMessage = 'This email address is already registered by another user.';
            } else {
              errorMessage = `Email: ${emailError}`;
            }
          }
          // Show image validation/storage errors instead of a generic save error.
          else if (result.errors.profile_picture) {
            const pictureError = Array.isArray(result.errors.profile_picture)
              ? result.errors.profile_picture[0]
              : result.errors.profile_picture;
            errorMessage = `Profile picture: ${pictureError}`;
          }
          // General error handling
          else if (result.errors.general) {
            errorMessage = Array.isArray(result.errors.general)
              ? result.errors.general[0]
              : result.errors.general;
          }
          // Use the result message if available
          else if (result.message) {
            errorMessage = result.message;
          }
        }

        setError(errorMessage);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else if (error.response?.data?.errors) {
        const firstError = Object.values(error.response.data.errors).flat()[0];
        setError(firstError || 'Failed to update profile. Please check your details and try again.');
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser.');
      return;
    }

    setLocationLoading(true);
    setError('');

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        try {
          const newLocationData = {
            latitude,
            longitude,
            accuracy,
            consent: true,
            nearest_area: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
          };

          await api.post('/auth/update-location/', newLocationData);
          if (typeof window.scrollTo === 'function') window.scrollTo(0, 0);
          setLocationData({ ...newLocationData, timestamp: new Date().toISOString() });
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
        } catch (error) {
          const message = error.response?.data?.errors?.location || error.response?.data?.detail || error.message;
          setError(`Failed to update location: ${message}`);
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        setLocationLoading(false);
        setError('Failed to get location. Please check your browser permissions.');
      },
      options
    );
  };

  // Scroll to top when success state changes
  useEffect(() => {
    if (success) {
      if (typeof window.scrollTo === 'function') {
        window.scrollTo(0, 0);
      }
    }
  }, [success]);

  // Push notification handlers
  const handlePushNotificationToggle = async (checked) => {
    setNotificationLoading(true);
    try {
      if (checked) {
        const result = await enablePushNotifications();
        if (result.success) {
          setNotificationPreferences(prev => ({ ...prev, push_notifications: true }));
        } else {
          setError('Failed to enable push notifications: ' + (result.error || 'Unknown error'));
          setNotificationPreferences(prev => ({ ...prev, push_notifications: false }));
        }
      } else {
        const result = await disablePushNotifications();
        if (result.success) {
          setNotificationPreferences(prev => ({ ...prev, push_notifications: false }));
        } else {
          setError('Failed to disable push notifications: ' + (result.error || 'Unknown error'));
          setNotificationPreferences(prev => ({ ...prev, push_notifications: true }));
        }
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      setError('Failed to update push notification settings');
      setNotificationPreferences(prev => ({ ...prev, push_notifications: !checked }));
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleNotificationTypeToggle = (type, checked) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [type]: checked
    }));
  };

  // CV Download Handler
  const handleDownloadCV = async (format) => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess(false);
      
      console.log(`📄 Downloading CV in ${format} format...`);
      // Use cv_format to avoid DRF 'format' keyword conflict
      const response = await makeAuthenticatedRequest(`/api/auth/cv/?cv_format=${format}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let errorMessage = 'Failed to download CV. Please try again.';

        if (contentType.includes('application/json')) {
          const errorData = await response.json().catch(() => null);
          errorMessage = errorData?.detail || errorData?.error || errorData?.message || errorMessage;
        } else {
          const text = await response.text().catch(() => '');
          if (text) {
            errorMessage = text;
          }
        }

        console.error('❌ CV Download failed:', response.status, errorMessage);
        setError(errorMessage);
        return;
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        throw new Error('The server returned an empty CV file.');
      }

      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^";]+)(?:;|$)/i);
      const fallbackFilename = `My_CV.${format}`;
      let downloadFilename = fallbackFilename;
      if (filenameMatch?.[1]) {
        try {
          downloadFilename = decodeURIComponent(filenameMatch[1].replace(/"/g, '').trim());
        } catch (decodeError) {
          console.warn('Could not decode CV filename from response headers:', decodeError);
          downloadFilename = filenameMatch[1].replace(/"/g, '').trim() || fallbackFilename;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      document.body.removeChild(a);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      console.log('✅ CV Download successful');
    } catch (err) {
      console.error('❌ CV Download Error:', err);
      setError(err?.message || 'An error occurred while downloading CV');
    } finally {
      setSubmitting(false);
    }
  };

  const normalizedSkillSearch = skillSearch.trim().toLowerCase();
  const skillCategoryOptions = lmsSkillCategoryOptions(skills);
  const filteredUserSkills = userSkills.filter((userSkill) => {
    const skillName = (userSkill.custom_skill_name || userSkill.skill_name || '').toLowerCase();
    const skillCategory = userSkill.skill_lms_category
      ? lmsSkillCategoryKey(userSkill)
      : canonicalSkillCategory(userSkill.skill_category, skillName);

    return (
      (!normalizedSkillSearch || skillName.includes(normalizedSkillSearch)) &&
      (!selectedCategory || skillCategory === selectedCategory)
    );
  });
  const availableSkills = availableCatalogueSkills(skills, userSkills);
  const pickerSkills = skills.filter(skill => availableSkills.includes(skill) || String(skill.id) === String(editingSkill?.skill));
  const filteredAvailableSkills = filterCatalogueSkills(availableSkills, skillSearch, selectedCategory);
  const selectedCategoryLabel = skillCategoryOptions.find(category => category.id === selectedCategory)?.label || skillCategoryLabel(selectedCategory);
  const chooseCatalogueSkill = (skill) => {
    resetSkillModal();
    setNewSkill(previous => ({ ...previous, skill: skill.id }));
    setModalSkillSearch(skill.name);
    setShowAddSkill(true);
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Header */}
        <div className="profile-header">
          <Link to="/dashboard" className="back-link">
            <ArrowLeftIcon className="back-icon" />
            Back to Dashboard
          </Link>
          
          <div className="header-card">
            <div className="header-content">
              <div className="header-profile-section">
                <div className="header-profile-picture">
                  <ProfilePictureUpload
                    currentImage={profilePicturePreview}
                    onImageChange={handleProfilePictureChange}
                    onImageRemove={handleProfilePictureRemove}
                    size="medium"
                    disabled={submitting}
                    showRequirementsInOverlay={true}
                  />
                  <p className="profile-picture-save-hint" role="status">
                    {profilePicture
                      ? 'Photo selected. Click “Update Profile” below to upload it.'
                      : 'Choose a photo, then click “Update Profile” below to save it.'}
                  </p>
                </div>
                <div className="header-text">
                  <h1 className="header-title">My Profile</h1>
                  <h2 className="header-course-name">{user?.first_name} {user?.last_name}</h2>
                  <p className="header-details">
                    <span className="header-detail-item">Email:</span> {user?.email} |
                    <span className="header-detail-item">Status:</span> {user?.is_verified ? 'Verified' : 'Unverified'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Success Banner */}
        {success && (
          <div className="success-banner">
            <div className="success-banner-content">
              <CheckCircleIcon className="success-banner-icon" />
              <div className="success-banner-text">
                <h3 className="success-banner-title">
                  {typeof success === 'object' ? success.message : 'Profile Updated Successfully!'}
                </h3>
                <div className="success-banner-message">
                  {typeof success === 'object' && success.updatedFields?.length > 0 ? (
                    <div>
                      <p style={{ marginBottom: '8px' }}>
                        <strong>Updated fields:</strong> {success.updatedFields.join(', ')}
                      </p>
                      {success.profilePictureUpdated && (
                        <p style={{
                          color: '#059669',
                          fontWeight: '600',
                          marginBottom: '4px'
                        }}>
                          ✅ Profile picture uploaded successfully!
                        </p>
                      )}
                      <p style={{ fontSize: '0.9rem', opacity: '0.8' }}>
                        All changes have been saved to your account.
                      </p>
                    </div>
                  ) : (
                    <p>Your profile information has been saved.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="profile-grid">
          {/* Profile Form */}
          <div className="form-section">
            <div className="form-header">
              <DocumentTextIcon className="form-header-icon" />
              <h3 className="form-header-title">Profile Information</h3>
            </div>

            <div className="form-content">
              {/* Section Navigation */}
              <div className="section-nav" role="tablist" aria-label="Profile sections">
                <button
                  className={`section-nav-item ${activeSection === 'personal' ? 'active' : ''}`}
                  onClick={() => setActiveSection('personal')}
                  role="tab"
                  aria-selected={activeSection === 'personal'}
                  aria-controls="personal-section"
                  aria-label="Personal information section"
                >
                  <UserIcon className="section-nav-icon" aria-hidden="true" />
                  <span>Personal Info</span>
                </button>
                <button
                  className={`section-nav-item ${activeSection === 'education' ? 'active' : ''}`}
                  onClick={() => setActiveSection('education')}
                  role="tab"
                  aria-selected={activeSection === 'education'}
                  aria-controls="education-section"
                  aria-label="Education background section"
                >
                  <AcademicCapIcon className="section-nav-icon" aria-hidden="true" />
                  <span>Education</span>
                </button>
                <button
                  className={`section-nav-item ${activeSection === 'skills' ? 'active' : ''}`}
                  onClick={() => setActiveSection('skills')}
                  role="tab"
                  aria-selected={activeSection === 'skills'}
                  aria-controls="skills-section"
                  aria-label="Skills and competencies section"
                >
                  <CpuChipIcon className="section-nav-icon" aria-hidden="true" />
                  <span>Skills</span>
                </button>

                <button
                  type="button"
                  className={`section-nav-item ${activeSection === 'cv' ? 'active' : ''}`}
                  onClick={() => setActiveSection('cv')}
                  role="tab"
                  aria-selected={activeSection === 'cv'}
                  aria-controls="cv-section"
                  aria-label="Digital CV section"
                >
                  <DocumentTextIcon className="section-nav-icon" aria-hidden="true" />
                  <span>Digital CV</span>
                </button>
              </div>

              {error && (
                <div className="form-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="profile-form">
                {/* Personal Information Section */}
                {activeSection === 'personal' && (
                  <div className="form-section-content">
                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="first_name" className="field-label">
                          First Name <span className="required-indicator">*</span>
                        </label>
                        <input
                          type="text"
                          id="first_name"
                          name="first_name"
                          required
                          className="field-input"
                          value={formData.first_name}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-field">
                        <label htmlFor="last_name" className="field-label">
                          Last Name <span className="required-indicator">*</span>
                        </label>
                        <input
                          type="text"
                          id="last_name"
                          name="last_name"
                          required
                          className="field-input"
                          value={formData.last_name}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="email" className="field-label">
                          Email <span className="required-indicator">*</span>
                        </label>
                        <div className="input-container">
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            className={`field-input ${
                              fieldValidation.email.isValid === true ? 'field-input-valid' :
                              fieldValidation.email.isValid === false ? 'field-input-invalid' : ''
                            }`}
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="your.email@example.com"
                          />
                          {fieldValidation.email.isValid === true && (
                            <div className="field-validation-icon field-validation-success">
                              <CheckCircleIcon className="validation-icon" />
                            </div>
                          )}
                          {fieldValidation.email.isValid === false && (
                            <div className="field-validation-icon field-validation-error">
                              <ExclamationTriangleIcon className="validation-icon" />
                            </div>
                          )}
                        </div>

                        {fieldValidation.email.isValid === false && fieldValidation.email.error && (
                          <div className="field-error">
                            {fieldValidation.email.error}
                          </div>
                        )}
                      </div>

                      <div className="form-field">
                        <label htmlFor="phone_number" className="field-label">
                          Phone Number
                          <span className="field-hint">
                            (10 digits, supports +27)
                          </span>
                        </label>
                        <div className="input-container">
                          <input
                            type="tel"
                            id="phone_number"
                            name="phone_number"
                            className={`field-input ${
                              fieldValidation.phone_number.isValid === true ? 'field-input-valid' :
                              fieldValidation.phone_number.isValid === false ? 'field-input-invalid' : ''
                            }`}
                            value={formData.phone_number}
                            onChange={handleInputChange}
                            placeholder="0123456789 or +27123456789"
                          />
                          {fieldValidation.phone_number.isValid === true && (
                            <div className="field-validation-icon field-validation-success">
                              <CheckCircleIcon className="validation-icon" />
                            </div>
                          )}
                          {fieldValidation.phone_number.isValid === false && (
                            <div className="field-validation-icon field-validation-error">
                              <ExclamationTriangleIcon className="validation-icon" />
                            </div>
                          )}
                        </div>

                        {fieldValidation.phone_number.isValid === false && fieldValidation.phone_number.error && (
                          <div className="field-error">
                            {fieldValidation.phone_number.error}
                          </div>
                        )}

                        {fieldValidation.phone_number.isValid === true && formData.phone_number && (
                          <div className="field-info">
                            <InformationCircleIcon className="field-info-icon" />
                            Formatted: {formatPhoneNumberDisplay(formData.phone_number)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="id_number" className="field-label">
                          ID Number
                          <span className="field-hint">
                            (Required for course applications)
                          </span>
                        </label>
                        <div className="id-input-container">
                          <input
                            type="text"
                            id="id_number"
                            name="id_number"
                            className={`field-input ${
                              idValidation.serverError ? 'field-input-invalid' :
                              idValidation.isValid === true ? 'field-input-valid' :
                              idValidation.isValid === false ? 'field-input-invalid' : ''
                            }`}
                            value={formData.id_number}
                            onChange={handleInputChange}
                            placeholder="0000000000000"
                            maxLength="13"
                            pattern="[0-9]{13}"
                          />
                          {idValidation.isValid === true && !idValidation.serverError && (
                            <div className="field-validation-icon field-validation-success">
                              <CheckCircleIcon className="validation-icon" />
                            </div>
                          )}
                          {(idValidation.isValid === false || idValidation.serverError) && (
                            <div className="field-validation-icon field-validation-error">
                              <ExclamationTriangleIcon className="validation-icon" />
                            </div>
                          )}
                        </div>

                        {/* Validation Messages */}
                        {idValidation.serverError && (
                          <div className="field-error">
                            {idValidation.serverError}
                          </div>
                        )}

                        {!idValidation.serverError && idValidation.isValid === false && idValidation.errors.length > 0 && (
                          <div className="field-error">
                            {getIdValidationMessage(idValidation.errors)}
                          </div>
                        )}

                        {formData.id_number && idValidation.isValid === true && !idValidation.serverError && (
                          <div className="field-info">
                            <InformationCircleIcon className="field-info-icon" />
                            Valid South African ID number
                          </div>
                        )}
                      </div>

                      <div className="form-field">
                        <label htmlFor="date_of_birth" className="field-label">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          id="date_of_birth"
                          name="date_of_birth"
                          className="field-input"
                          value={formData.date_of_birth}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="gender" className="field-label">
                          Gender
                        </label>
                        <select
                          id="gender"
                          name="gender"
                          className="field-input"
                          value={formData.gender}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                          <option value="prefer_not_to_say">Prefer not to say</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label htmlFor="race" className="field-label">
                          Race
                        </label>
                        <select
                          id="race"
                          name="race"
                          className="field-input"
                          value={formData.race}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Race</option>
                          <option value="african">African</option>
                          <option value="coloured">Coloured</option>
                          <option value="indian">Indian</option>
                          <option value="white">White</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-field">
                      <label htmlFor="address" className="field-label">
                        Address
                      </label>
                      <textarea
                        id="address"
                        name="address"
                        rows={3}
                        className="field-textarea"
                        value={formData.address}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="city" className="field-label">
                          City
                        </label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          className="field-input"
                          value={formData.city}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="form-field">
                        <label htmlFor="province" className="field-label">
                          Province
                        </label>
                        <select
                          id="province"
                          name="province"
                          className="field-input"
                          value={formData.province}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Province</option>
                          <option value="gauteng">Gauteng</option>
                          <option value="western_cape">Western Cape</option>
                          <option value="kwazulu_natal">KwaZulu-Natal</option>
                          <option value="eastern_cape">Eastern Cape</option>
                          <option value="free_state">Free State</option>
                          <option value="limpopo">Limpopo</option>
                          <option value="mpumalanga">Mpumalanga</option>
                          <option value="north_west">North West</option>
                          <option value="northern_cape">Northern Cape</option>
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="postal_code" className="field-label">
                          Postal Code <span className="optional-indicator">Optional</span>
                        </label>
                        <input
                          type="text"
                          id="postal_code"
                          name="postal_code"
                          className="field-input"
                          value={formData.postal_code}
                          onChange={handleInputChange}
                          placeholder="e.g. 2000"
                        />
                      </div>
                      <div className="form-field">
                        <label className="field-label">
                          Economic Corridor
                        </label>
                        <div className="economic-corridor-display">
                          {getEconomicCorridor(formData.postal_code) || 'Enter postal code to see corridor'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Digital CV Section */}
                {activeSection === 'cv' && (
                  <div className="form-section-content">
                    <div className="cv-section-container" style={{ padding: '20px', textAlign: 'center' }}>
                      <div className="cv-header" style={{ marginBottom: '30px' }}>
                        <h3 style={{ fontSize: '1.5rem', marginBottom: '10px', color: '#1f2937' }}>Build Your Digital CV</h3>
                        <p style={{ color: '#6b7280' }}>
                          {isEditingCv 
                            ? 'Edit your CV content below and generate a custom PDF.' 
                            : 'Generate a professional CV based on your profile information. You can download it in DOCX or PDF format.'}
                        </p>
                      </div>
                      
                      {isEditingCv ? (
                        <div className="cv-editor-container" style={{ textAlign: 'left', maxWidth: '800px', margin: '0 auto' }}>
                          <ReactQuill 
                            theme="snow" 
                            value={cvHtml} 
                            onChange={setCvHtml} 
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                                ['link', 'image'],
                                ['clean']
                              ],
                            }}
                            style={{ height: '500px', marginBottom: '50px', backgroundColor: 'white' }}
                          />
                          <div className="cv-editor-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '60px' }}>
                            <button
                              type="button"
                              onClick={handleCancelEditCv}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: '#f3f4f6',
                                color: '#4b5563',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                cursor: 'pointer'
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveCustomCv}
                              disabled={isGeneratingCustomCv}
                              style={{
                                padding: '10px 20px',
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isGeneratingCustomCv ? 'not-allowed' : 'pointer',
                                opacity: isGeneratingCustomCv ? 0.7 : 1
                              }}
                            >
                              {isGeneratingCustomCv ? 'Generating PDF...' : 'Generate PDF'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="cv-actions" style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '30px' }}>
                            <div className="cv-action-card" style={{ 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '12px', 
                              padding: '24px', 
                              width: '240px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              backgroundColor: '#ffffff',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}>
                              <div className="cv-icon-container" style={{ 
                                width: '64px', 
                                height: '64px', 
                                borderRadius: '50%', 
                                backgroundColor: '#eff6ff', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                marginBottom: '16px',
                                color: '#2563eb'
                              }}>
                                <DocumentTextIcon className="cv-icon" style={{ width: '32px', height: '32px' }} />
                              </div>
                              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>Word Document</h4>
                              <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.875rem' }}>Editable format (.docx)</p>
                              <button 
                                type="button" 
                                className="cv-download-btn"
                                onClick={() => handleDownloadCV('docx')}
                                disabled={submitting}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  backgroundColor: '#2563eb',
                                  color: 'white',
                                  borderRadius: '8px',
                                  fontWeight: '500',
                                  border: 'none',
                                  cursor: submitting ? 'not-allowed' : 'pointer',
                                  opacity: submitting ? 0.7 : 1
                                }}
                              >
                                {submitting ? 'Generating...' : 'Download DOCX'}
                              </button>
                            </div>

                            <div className="cv-action-card" style={{ 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '12px', 
                              padding: '24px', 
                              width: '240px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              backgroundColor: '#ffffff',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}>
                              <div className="cv-icon-container" style={{ 
                                width: '64px', 
                                height: '64px', 
                                borderRadius: '50%', 
                                backgroundColor: '#fef2f2', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                marginBottom: '16px',
                                color: '#dc2626'
                              }}>
                                <DocumentTextIcon className="cv-icon" style={{ width: '32px', height: '32px' }} />
                              </div>
                              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>PDF Document</h4>
                              <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.875rem' }}>Print-ready format (.pdf)</p>
                              <button 
                                type="button" 
                                className="cv-download-btn"
                                onClick={() => handleDownloadCV('pdf')}
                                disabled={submitting}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  backgroundColor: '#dc2626',
                                  color: 'white',
                                  borderRadius: '8px',
                                  fontWeight: '500',
                                  border: 'none',
                                  cursor: submitting ? 'not-allowed' : 'pointer',
                                  opacity: submitting ? 0.7 : 1
                                }}
                              >
                                {submitting ? 'Generating...' : 'Download PDF'}
                              </button>
                            </div>

                            {/* New Custom Edit Card */}
                            <div className="cv-action-card" style={{ 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '12px', 
                              padding: '24px', 
                              width: '240px',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              backgroundColor: '#ffffff',
                              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                            }}>
                              <div className="cv-icon-container" style={{ 
                                width: '64px', 
                                height: '64px', 
                                borderRadius: '50%', 
                                backgroundColor: '#f0fdf4', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                marginBottom: '16px',
                                color: '#16a34a'
                              }}>
                                <PencilIcon className="cv-icon" style={{ width: '32px', height: '32px' }} />
                              </div>
                              <h4 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>Custom Edit</h4>
                              <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '0.875rem' }}>Edit content & Generate PDF</p>
                              <button 
                                type="button" 
                                className="cv-download-btn"
                                onClick={handleEditCv}
                                disabled={cvLoading}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  backgroundColor: '#16a34a',
                                  color: 'white',
                                  borderRadius: '8px',
                                  fontWeight: '500',
                                  border: 'none',
                                  cursor: cvLoading ? 'not-allowed' : 'pointer',
                                  opacity: cvLoading ? 0.7 : 1
                                }}
                              >
                                {cvLoading ? 'Loading...' : 'Edit & Generate'}
                              </button>
                            </div>
                          </div>

                          <div className="cv-info-note" style={{ 
                            backgroundColor: '#f3f4f6', 
                            padding: '16px', 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'start', 
                            textAlign: 'left',
                            maxWidth: '600px',
                            margin: '0 auto'
                          }}>
                            <InformationCircleIcon className="info-icon" style={{ width: '24px', height: '24px', marginRight: '12px', color: '#4b5563', flexShrink: 0 }} />
                            <p style={{ fontSize: '0.875rem', color: '#4b5563', margin: 0 }}>
                              <strong>Note:</strong> Ensure your profile information (Personal, Education, Skills) is up to date before generating your CV to get the best result.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Education Section */}
                {activeSection === 'education' && (
                  <div className="form-section-content">
                    <h4 className="section-subtitle">Basic Education</h4>

                    <div className="form-row">
                      <div className="form-field">
                        <label htmlFor="highest_grade" className="field-label">
                          Highest Grade Completed
                        </label>
                        <select
                          id="highest_grade"
                          name="highest_grade"
                          className="field-input"
                          value={formData.highest_grade}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Grade</option>
                          <option value="grade_8">Grade 8</option>
                          <option value="grade_9">Grade 9</option>
                          <option value="grade_10">Grade 10</option>
                          <option value="grade_11">Grade 11</option>
                          <option value="grade_12">Grade 12</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label htmlFor="matric_year" className="field-label">
                          Matric Year
                        </label>
                        <input
                          type="number"
                          id="matric_year"
                          name="matric_year"
                          className="field-input"
                          min="1980"
                          max={new Date().getFullYear()}
                          value={formData.matric_year}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="form-field">
                      <label htmlFor="school_name" className="field-label">
                        School Name
                      </label>
                      <input
                        type="text"
                        id="school_name"
                        name="school_name"
                        className="field-input"
                        value={formData.school_name}
                        onChange={handleInputChange}
                      />
                    </div>

                    {/* Matric Subjects Management */}
                    <MatricSubjects
                      user={user}
                    />

                    {/* Tertiary Education Management */}
                    <TertiaryEducation
                      user={user}
                    />
                  </div>
                )}

                {/* Skills Section */}
                {activeSection === 'skills' && (
                  <div className="form-section-content">
                    <div className="skills-section">
                      <div className="skills-header">
                        <h4 className="section-subtitle">My Skills & Competencies</h4>
                        <p className="section-description">
                          Add your skills to help GCRA match you with relevant training programs and opportunities!
                        </p>
                        <div className="skills-header-meta" aria-live="polite">
                          <span><strong>{userSkills.length}</strong> added to your profile</span>
                          <span>{skillsLoading ? 'Loading skill catalogue…' : <><strong>{availableSkills.length}</strong> catalogue skills available to add</>}</span>
                        </div>
                      </div>

                      <div className="profile-skills-tabs" role="group" aria-label="Skills view">
                        <button type="button" aria-pressed={skillsView === 'owned'} onClick={() => setSkillsView('owned')}>My skills <span>{userSkills.length}</span></button>
                        <button type="button" aria-pressed={skillsView === 'catalogue'} onClick={() => setSkillsView('catalogue')}>Browse skill catalogue <span>{availableSkills.length}</span></button>
                      </div>
                      <p className="profile-skills-explainer">{skillsView === 'owned'
                        ? 'These are skills on your profile, not the full catalogue. Browse the catalogue to find more skills to add.'
                        : 'Explore the shared NSMobi and LMS skills pool. Add only skills you have, then tell us your proficiency. Browsing does not award a skill.'}</p>

                      {/* Skills Search and Filter */}
                      <div className="skills-controls">
                        <div className="skills-search">
                          <div className="search-input-container">
                            <MagnifyingGlassIcon className="search-icon" />
                            <input
                              type="text"
                              placeholder="Search skills..."
                              aria-label={skillsView === 'owned' ? 'Search my skills' : 'Search skill catalogue'}
                              value={skillSearch}
                              onChange={(e) => { setSkillSearch(e.target.value); setCatalogueLimit(24); }}
                              className="search-input"
                            />
                          </div>
                        </div>

                        <div className="skills-filter">
                          <select
                            value={selectedCategory}
                            onChange={(e) => { setSelectedCategory(e.target.value); setCatalogueLimit(24); }}
                            aria-label="Skill category"
                            className="filter-select"
                          >
                            <option value="">All Categories</option>
                            {skillCategoryOptions.map(category => <option key={category.id} value={category.id}>{category.label}</option>)}
                          </select>
                        </div>

                        <button
                          type="button"
                          onClick={openSkillModal}
                          className="add-skill-btn"
                        >
                          <PlusIcon className="btn-icon" />
                          Add Skill
                        </button>
                      </div>

                      {/* User Skills List */}
                      {skillsView === 'owned' ? <div className="user-skills-list">
                        {userSkillsLoading ? (
                          <div className="skills-loading-state" role="status">Loading your skills…</div>
                        ) : userSkills.length > 0 ? (
                          filteredUserSkills.length > 0 ? (
                            <div className="profile-skills-grid">
                            {filteredUserSkills.map((userSkill) => (
                              <article key={userSkill.id} className="profile-skill-card">
                                <div className="profile-skill-card-header">
                                  <div className="profile-skill-heading">
                                    <span className="profile-skill-category-label">
                                      {userSkill.skill_lms_category
                                        ? lmsSkillCategoryLabel(userSkill)
                                        : skillCategoryLabel(userSkill.skill_category, userSkill.custom_skill_name || userSkill.skill_name)}
                                    </span>
                                    <h5 className="profile-skill-name">
                                    {userSkill.custom_skill_name || userSkill.skill_name || 'Unknown Skill'}
                                    </h5>
                                  </div>
                                  <div className="profile-skill-card-actions">
                                    <button
                                      type="button"
                                      onClick={() => editUserSkill(userSkill)}
                                      className="profile-edit-skill-btn"
                                      title="Edit skill"
                                      aria-label={`Edit ${userSkill.custom_skill_name || userSkill.skill_name || 'skill'}`}
                                    >
                                      <PencilIcon className="profile-edit-icon" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeUserSkill(userSkill.id)}
                                      className="profile-remove-skill-btn"
                                      title="Remove skill"
                                      aria-label={`Remove ${userSkill.custom_skill_name || userSkill.skill_name || 'skill'}`}
                                    >
                                      <XMarkIcon className="profile-remove-icon" />
                                    </button>
                                  </div>
                                </div>
                                <div className="profile-skill-details">
                                  {userSkill.lms_score !== null && userSkill.lms_score !== undefined ? (
                                    <div className="profile-skill-experience">
                                      <span className="profile-skill-detail-label">LMS evidence</span>
                                      <span>Earned skill · {Math.round(userSkill.lms_score)}%</span>
                                    </div>
                                  ) : null}
                                  <div className="profile-skill-proficiency">
                                    <span className="profile-skill-detail-label">Proficiency</span>
                                    <span className={`profile-proficiency-badge proficiency-${userSkill.proficiency_level}`}>
                                      {userSkill.proficiency_display || userSkill.proficiency_level}
                                    </span>
                                  </div>
                                  {userSkill.years_experience !== null && userSkill.years_experience !== undefined && userSkill.years_experience !== '' && (
                                    <div className="profile-skill-experience">
                                      <span className="profile-skill-detail-label">Experience</span>
                                      {userSkill.years_experience} years experience
                                    </div>
                                  )}
                                  {userSkill.notes && (
                                    <div className="profile-skill-notes" title={userSkill.notes}>
                                      {userSkill.notes}
                                    </div>
                                  )}
                                </div>
                              </article>
                            ))}
                            </div>
                          ) : (
                            <div className="skills-empty-state skills-filter-empty-state">
                              <CpuChipIcon className="skills-empty-icon" />
                              <p className="skills-empty-text">No matching skills on your profile</p>
                              <p className="skills-empty-description">The shared catalogue may have more skills in this category.</p>
                              <button type="button" className="clear-skills-filter-btn" onClick={() => setSkillsView('catalogue')}>Browse available skills</button>
                              <button
                                type="button"
                                className="clear-skills-filter-btn"
                                onClick={() => {
                                  setSkillSearch('');
                                  setSelectedCategory('');
                                }}
                              >
                                Clear filters
                              </button>
                            </div>
                          )
                        ) : (
                          <div className="skills-empty-state">
                            <CpuChipIcon className="skills-empty-icon" />
                            <p className="skills-empty-text">No skills added yet!</p>
                            <p className="skills-empty-description">
                              Add your skills to showcase your abilities and help GCRA find the best training programs for you!
                            </p>
                          </div>
                        )}
                      </div> : <section className="profile-skill-catalogue" aria-label="Available skills catalogue">
                        {skillsLoading ? <p role="status">Loading shared skill catalogue…</p> : catalogueError ?
                          <div role="alert"><p>{catalogueError}</p><button type="button" onClick={fetchSkills}>Try again</button></div> : <>
                          <p className="profile-catalogue-count" role="status">{filteredAvailableSkills.length} skills available{selectedCategory ? ` in ${selectedCategoryLabel}` : ''} · {userSkills.length} already on your profile</p>
                          {filteredAvailableSkills.length ? <div className="profile-catalogue-grid">
                            {filteredAvailableSkills.slice(0, catalogueLimit).map(skill => <article className="profile-catalogue-card" key={skill.id}>
                              <span>{lmsSkillCategoryLabel(skill)}</span>
                              <h5>{skill.name}</h5>
                              {skill.description ? <p>{skill.description}</p> : null}
                              <button type="button" onClick={() => chooseCatalogueSkill(skill)} aria-label={`Add ${skill.name} to my skills`}><PlusIcon aria-hidden="true" /> Add to my skills</button>
                            </article>)}
                          </div> : <p>No available catalogue skills match these filters. Skills you have already added are under My skills.</p>}
                          {filteredAvailableSkills.length > catalogueLimit ? <button type="button" className="clear-skills-filter-btn" onClick={() => setCatalogueLimit(limit => limit + 24)}>Show more skills ({filteredAvailableSkills.length - catalogueLimit} remaining)</button> : null}
                        </>}
                      </section>}

                      {/* Enhanced Add Skill Modal */}
                      {showAddSkill && (
                        <div className="modal-overlay" onClick={closeSkillModal}>
                          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                              <h3 className="modal-title">{editingSkill ? 'Edit Skill' : 'Add New Skill'}</h3>
                              <button
                                type="button"
                                onClick={closeSkillModal}
                                className="modal-close-btn"
                                disabled={skillModalLoading}
                              >
                                <XMarkIcon className="close-icon" />
                              </button>
                            </div>

                            {/* Success/Error Messages */}
                            {skillModalSuccess && (
                              <div className="modal-message success-message">
                                <div className="message-content">
                                  <span className="message-text">{skillModalSuccess}</span>
                                </div>
                              </div>
                            )}

                            {skillModalError && (
                              <div className="modal-message error-message">
                                <div className="message-content">
                                  <span className="message-text">{skillModalError}</span>
                                </div>
                              </div>
                            )}

                            <div className="modal-body">
                              <div className="skill-form">
                                <div className="form-field">
                                  <label className="field-label">Skill Type</label>
                                  <div className="skill-type-options">
                                    <label className="radio-option">
                                      <input
                                        type="radio"
                                        name="skillType"
                                        value="predefined"
                                        checked={skillType === 'predefined'}
                                        onChange={() => {
                                          setSkillType('predefined');
                                          setNewSkill(prev => ({ ...prev, custom_skill_name: '', skill: '' }));
                                          setSkillValidation(prev => ({
                                            ...prev,
                                            skill: { isValid: true, error: '' },
                                            custom_skill_name: { isValid: true, error: '' }
                                          }));
                                        }}
                                        disabled={skillModalLoading}
                                      />
                                      <span>Choose from list</span>
                                    </label>
                                    <label className="radio-option">
                                      <input
                                        type="radio"
                                        name="skillType"
                                        value="custom"
                                        checked={skillType === 'custom'}
                                        onChange={() => {
                                          setSkillType('custom');
                                          setNewSkill(prev => ({ ...prev, skill: '', custom_skill_name: '' }));
                                          setSkillValidation(prev => ({
                                            ...prev,
                                            skill: { isValid: true, error: '' },
                                            custom_skill_name: { isValid: true, error: '' }
                                          }));
                                        }}
                                        disabled={skillModalLoading}
                                      />
                                      <span>Add custom skill</span>
                                    </label>
                                  </div>
                                </div>

                                {skillType === 'predefined' ? (
                                  <div className="form-field" ref={searchContainerRef}>
                                    <label className="field-label">
                                      Select Skill
                                      <span className="skill-picker-count">
                                        {skillsLoading ? 'Loading…' : `${pickerSkills.length} available`}
                                      </span>
                                    </label>
                                    <div className="custom-autocomplete-container">
                                      <MagnifyingGlassIcon 
                                        className="search-icon" 
                                        style={{ 
                                          position: 'absolute', 
                                          left: '12px', 
                                          top: '50%', 
                                          transform: 'translateY(-50%)', 
                                          width: '18px', 
                                          height: '18px', 
                                          color: '#9ca3af',
                                          zIndex: 10
                                        }} 
                                      />
                                      <input
                                        type="text"
                                        value={modalSkillSearch}
                                        onChange={(e) => {
                                          setModalSkillSearch(e.target.value);
                                          setIsDropdownOpen(true);
                                          if (!e.target.value) {
                                            setNewSkill(prev => ({ ...prev, skill: '' }));
                                          }
                                        }}
                                        onFocus={() => setIsDropdownOpen(true)}
                                        className={`custom-autocomplete-input ${!skillValidation.skill.isValid ? 'error' : ''}`}
                                        placeholder="Type to search skills..."
                                        disabled={skillModalLoading}
                                      />
                                      
                                      {isDropdownOpen && (
                                        <div className="custom-autocomplete-dropdown">
                                          {skillsLoading ? (
                                            <div className="custom-autocomplete-status">Loading available skills…</div>
                                          ) : pickerSkills.filter(skill =>
                                            (!selectedCategory || lmsSkillCategoryKey(skill) === selectedCategory) &&
                                            (!modalSkillSearch || skill.name.toLowerCase().includes(modalSkillSearch.toLowerCase()))
                                          ).length > 0 ? (
                                            pickerSkills
                                              .filter(skill =>
                                                (!selectedCategory || lmsSkillCategoryKey(skill) === selectedCategory) &&
                                                (!modalSkillSearch || skill.name.toLowerCase().includes(modalSkillSearch.toLowerCase()))
                                              )
                                              .map(skill => (
                                                <div 
                                                  key={skill.id} 
                                                  className="custom-autocomplete-item"
                                                  onClick={() => {
                                                    setNewSkill(prev => ({ ...prev, skill: skill.id }));
                                                    setModalSkillSearch(skill.name);
                                                    setSkillValidation(prev => ({
                                                      ...prev,
                                                      skill: { isValid: true, error: '' }
                                                    }));
                                                    setIsDropdownOpen(false);
                                                  }}
                                                >
                                                  <span className="profile-skill-option-name">{skill.name}</span>
                                                  <span className="item-category">{skill.category_display}</span>
                                                </div>
                                              ))
                                          ) : (
                                            <div 
                                              className="custom-autocomplete-item custom-autocomplete-action"
                                              onClick={() => {
                                                setSkillType('custom');
                                                setNewSkill(prev => ({ ...prev, custom_skill_name: modalSkillSearch, skill: '' }));
                                                setIsDropdownOpen(false);
                                              }}
                                            >
                                              <PlusIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                                              <span>Add "{modalSkillSearch}" as custom skill</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {!skillValidation.skill.isValid && (
                                      <div className="field-error">
                                        <span className="error-text">{skillValidation.skill.error}</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="form-field">
                                    <label className="field-label">Custom Skill Name</label>
                                    <input
                                      type="text"
                                      value={newSkill.custom_skill_name}
                                      onChange={(e) => {
                                        setNewSkill(prev => ({ ...prev, custom_skill_name: e.target.value }));
                                        setSkillValidation(prev => ({
                                          ...prev,
                                          custom_skill_name: { isValid: true, error: '' }
                                        }));
                                      }}
                                      className={`field-input ${!skillValidation.custom_skill_name.isValid ? 'error' : ''}`}
                                      placeholder="Enter skill name..."
                                      disabled={skillModalLoading}
                                    />
                                    {!skillValidation.custom_skill_name.isValid && (
                                      <div className="field-error">
                                        <span className="error-text">{skillValidation.custom_skill_name.error}</span>
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="form-field">
                                  <label className="field-label">Proficiency Level</label>
                                  <select
                                    value={newSkill.proficiency_level}
                                    onChange={(e) => {
                                      setNewSkill(prev => ({ ...prev, proficiency_level: e.target.value }));
                                      setSkillValidation(prev => ({
                                        ...prev,
                                        proficiency_level: { isValid: true, error: '' }
                                      }));
                                    }}
                                    className={`field-select ${!skillValidation.proficiency_level.isValid ? 'field-select-invalid' : ''}`}
                                    disabled={skillModalLoading}
                                  >
                                    <option value="">Choose your proficiency</option>
                                    <option value="beginner">🌱 Beginner</option>
                                    <option value="intermediate">🌿 Intermediate</option>
                                    <option value="advanced">🌳 Advanced</option>
                                    <option value="expert">🏆 Expert</option>
                                  </select>
                                  {!skillValidation.proficiency_level.isValid && (
                                    <div className="field-error">
                                      <span className="error-text">{skillValidation.proficiency_level.error}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="form-field">
                                  <label className="field-label">Years of Experience (Optional)</label>
                                  <input
                                    type="number"
                                    value={newSkill.years_experience}
                                    onChange={(e) => {
                                      setNewSkill(prev => ({ ...prev, years_experience: e.target.value }));
                                      setSkillValidation(prev => ({
                                        ...prev,
                                        years_experience: { isValid: true, error: '' }
                                      }));
                                    }}
                                    className={`field-input ${!skillValidation.years_experience.isValid ? 'error' : ''}`}
                                    placeholder="0"
                                    min="0"
                                    max="50"
                                    disabled={skillModalLoading}
                                  />
                                  {!skillValidation.years_experience.isValid && (
                                    <div className="field-error">
                                      <span className="error-text">{skillValidation.years_experience.error}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="form-field">
                                  <label className="field-label">Notes (Optional)</label>
                                  <textarea
                                    value={newSkill.notes}
                                    onChange={(e) => setNewSkill(prev => ({ ...prev, notes: e.target.value }))}
                                    className="field-textarea"
                                    rows="3"
                                    placeholder="Additional details about your experience with this skill..."
                                    disabled={skillModalLoading}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="modal-footer">
                              <button
                                type="button"
                                onClick={closeSkillModal}
                                className="cancel-button"
                                disabled={skillModalLoading}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleSkillSubmit}
                                className="submit-button"
                                disabled={skillModalLoading || (skillType === 'predefined' ? !newSkill.skill : !newSkill.custom_skill_name.trim())}
                              >
                                {skillModalLoading ? (
                                  <>
                                    <div className="loading-spinner-small"></div>
                                    {editingSkill ? 'Updating Skill...' : 'Adding Skill...'}
                                  </>
                                ) : (
                                  <>
                                    <PlusIcon className="btn-icon" />
                                    {editingSkill ? 'Update Skill' : 'Add Skill'}
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <Link to="/dashboard" className="cancel-button">
                    <ArrowLeftIcon className="back-icon" />
                    Cancel
                  </Link>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="submit-button"
                  >
                    {submitting ? (
                      <>
                        <div className="loading-spinner-small"></div>
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="submit-icon" />
                        Update Profile
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="profile-sidebar">
            {/* User Info */}
            <div className="sidebar-card">
              <div className="sidebar-header">
                <UserIcon className="sidebar-header-icon" />
                <h3 className="sidebar-header-title">Account Information</h3>
              </div>
              
              <div className="sidebar-content">
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-label">Member Since:</span>
                    <p className="info-value">{user?.date_joined ? new Date(user.date_joined).toLocaleDateString() : 'Unknown'}</p>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Account Status:</span>
                    <p className="info-value">{user?.is_verified ? 'Verified' : 'Unverified'}</p>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Role:</span>
                    <p className="info-value">{user?.role || 'Learner'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Info */}
            <div className="sidebar-card">
              <div className="sidebar-header">
                <MapPinIcon className="sidebar-header-icon" />
                <h3 className="sidebar-header-title">Location Services</h3>
              </div>
              
              <div className="sidebar-content">
                {locationData.latitude ? (
                  <div className="info-list">
                    <div className="info-item">
                      <span className="info-label">Current Location:</span>
                      <p className="info-value">{locationData.nearest_area}</p>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Last Updated:</span>
                      <p className="info-value">
                        {locationData.timestamp ? new Date(locationData.timestamp).toLocaleDateString() : 'Never'}
                      </p>
                    </div>
                    <button
                      onClick={getCurrentLocation}
                      disabled={locationLoading}
                      className="location-update-btn"
                    >
                      {locationLoading ? 'Updating...' : 'Update Location'}
                    </button>
                  </div>
                ) : (
                  <div className="location-empty">
                    <p className="empty-text">Location not shared</p>
                    <button
                      onClick={getCurrentLocation}
                      disabled={locationLoading}
                      className="location-share-btn"
                    >
                      {locationLoading ? 'Getting Location...' : 'Share Location'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Push Notification Settings */}
            <div className="sidebar-card">
              <div className="sidebar-header">
                <BellIcon className="sidebar-header-icon" />
                <h3 className="sidebar-header-title">Notification Settings</h3>
              </div>

              <div className="sidebar-content">
                <div className="notification-settings">
                  {/* Main Push Notifications Toggle */}
                  <div className="notification-item">
                    <div className="notification-item-header">
                      <label className="notification-label">
                        <input
                          type="checkbox"
                          checked={notificationPreferences.push_notifications}
                          onChange={(e) => handlePushNotificationToggle(e.target.checked)}
                          disabled={notificationLoading}
                          className="notification-checkbox"
                        />
                        <span className="notification-title">🔔 Push Notifications</span>
                      </label>
                      {notificationLoading && (
                        <div className="loading-spinner-small"></div>
                      )}
                    </div>
                    <p className="notification-description">
                      Receive real-time notifications in your browser
                    </p>
                  </div>

                  {/* Notification Type Preferences */}
                  {notificationPreferences.push_notifications && (
                    <div className="notification-types">
                      <h4 className="notification-types-title">Message Types:</h4>

                      <div className="notification-item">
                        <label className="notification-label">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.course_deadlines}
                            onChange={(e) => handleNotificationTypeToggle('course_deadlines', e.target.checked)}
                            className="notification-checkbox"
                          />
                          <span className="notification-title">📅 Course Deadlines</span>
                        </label>
                        <p className="notification-description">
                          Application deadlines and important dates
                        </p>
                      </div>

                      <div className="notification-item">
                        <label className="notification-label">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.system_updates}
                            onChange={(e) => handleNotificationTypeToggle('system_updates', e.target.checked)}
                            className="notification-checkbox"
                          />
                          <span className="notification-title">⚙️ System Updates</span>
                        </label>
                        <p className="notification-description">
                          Platform updates and maintenance notices
                        </p>
                      </div>

                      <div className="notification-item">
                        <label className="notification-label">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.training_opportunities}
                            onChange={(e) => handleNotificationTypeToggle('training_opportunities', e.target.checked)}
                            className="notification-checkbox"
                          />
                          <span className="notification-title">🎓 Training Opportunities</span>
                        </label>
                        <p className="notification-description">
                          New courses and training programs
                        </p>
                      </div>

                      <div className="notification-item">
                        <label className="notification-label">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.government_initiatives}
                            onChange={(e) => handleNotificationTypeToggle('government_initiatives', e.target.checked)}
                            className="notification-checkbox"
                          />
                          <span className="notification-title">🏛️ Government Initiatives</span>
                        </label>
                        <p className="notification-description">
                          GCRA and government program updates
                        </p>
                      </div>

                      <div className="notification-item">
                        <label className="notification-label">
                          <input
                            type="checkbox"
                            checked={notificationPreferences.general_news}
                            onChange={(e) => handleNotificationTypeToggle('general_news', e.target.checked)}
                            className="notification-checkbox"
                          />
                          <span className="notification-title">📰 General News</span>
                        </label>
                        <p className="notification-description">
                          General announcements and news updates
                        </p>
                      </div>
                    </div>
                  )}

                  {!notificationPreferences.push_notifications && (
                    <div className="notification-disabled">
                      <p className="notification-disabled-text">
                        Enable push notifications to customize message types
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Course Registrations */}
            <div className="sidebar-card">
              <div className="sidebar-header">
                <AcademicCapIcon className="sidebar-header-icon" />
                <h3 className="sidebar-header-title">My Course Registrations</h3>
              </div>

              <div className="sidebar-content">
                {registrationsLoading ? (
                  <div className="loading-state">
                    <div className="loading-spinner-small"></div>
                    <p className="loading-text">Loading registrations...</p>
                  </div>
                ) : registrations.length > 0 ? (
                  <div className="registrations-list">
                    {registrations.slice(0, 3).map((registration) => (
                      <div key={registration.id} className="registration-item">
                        <div className="registration-header">
                          <h4 className="registration-course-name">
                            {registration.course?.programme_name}
                          </h4>
                          <span className={`registration-status status-${registration.status}`}>
                            {registration.status_display || registration.status}
                          </span>
                        </div>
                        <div className="registration-details">
                          <p className="registration-campus">
                            {registration.course?.campus?.name} Campus
                          </p>
                          <p className="registration-date">
                            Applied: {new Date(registration.application_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    {registrations.length > 3 && (
                      <div className="registrations-more">
                        <Link to="/dashboard" className="view-all-link">
                          View all {registrations.length} registrations →
                        </Link>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="registrations-empty">
                    <p className="empty-text">No course registrations yet</p>
                    <Link to="/courses" className="empty-action-btn">
                      Browse Courses
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
